import { useClerk } from "@clerk/nextjs";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";

import WebSocket from "@/components/shared/socket/WebSocket";
import { AuthContext } from "@/data/auth/auth.context";
import { logger } from "@/util/logger";

interface ChatSocketProps {
  connected?: boolean;
  chatSocket?: WebSocket;
  socketConnecting?: boolean;
  emitEvent?: <T>(name: string, params: { [key: string]: any }, callback?: (response: T) => void) => void;
}

const ChatSocketContext = createContext<ChatSocketProps>({});

interface IProps {
  children: React.ReactNode;
}

export const ChatSocketProvider = ({ children }: IProps) => {
  let socket: WebSocket;
  const [chatSocket, setChatSocket] = useState<WebSocket>();
  const [connected, setConnected] = useState<boolean>(false);
  let socketConnecting = false;
  const { user } = AuthContext.useAuth();
  const { session } = useClerk();

  const connectToSocket = async () => {
    if (!session) {
      logger.warn("[ChatSocketProvider] No session available, cannot connect");
      return;
    }

    socketConnecting = true;
    const token = await session.getToken();
    if (!token) {
      logger.error("[ChatSocketProvider] Failed to get token");
      socketConnecting = false;
      return;
    }

    socket = new WebSocket({
      namespace: "chat",
      token,
    });

    socket.on("exception", (exception) => {
      // we make sure user is not falsely under the impression
      // that the content is being saved while it is not

      // todo rokpot toast an error when toasts will be up
      logger.error(exception);
    });

    socket.on("connect", () => {
      // if we're not connecting, we've been reconnected from a disconnect
      if (!socketConnecting) {
        // todo rokpot toast an success message when toasts will be up
      }
      setConnected(true);
      socketConnecting = false;
    });

    socket.on("disconnect", async () => {
      // if we're not connecting, we should be connected
      if (!socketConnecting) {
        // todo rokpot toast an disconnected message when toasts will be up
        setConnected(false);
      }
      if (session) {
        const newToken = await session.getToken();
        if (newToken) {
          socket.setAuthToken(newToken);
        }
      }
    });

    setChatSocket(socket);
  };

  const emitEvent = useCallback(
    <T,>(name: string, params: { [key: string]: any }, callback?: (response: T) => void) => {
      if (socketConnecting) {
        return;
      }

      // we don't make it loud, there's info about the connection status on the page
      if (!chatSocket?.connected) {
        return;
      }
      chatSocket.emit(name, params, callback);
    },
    [chatSocket, socketConnecting],
  );

  useEffect(() => {
    if (user && !chatSocket && session) {
      connectToSocket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session]);

  const contextValue = useMemo(
    () => ({
      connected,
      chatSocket,
      socketConnecting,
      emitEvent,
    }),
    [connected, chatSocket, socketConnecting, emitEvent],
  );

  return <ChatSocketContext.Provider value={contextValue}>{children}</ChatSocketContext.Provider>;
};

export default ChatSocketContext;
